import { useEffect } from 'react';
import { Outlet, useLoaderData, useMatches } from 'react-router';
import { useSetAtom } from 'jotai';
import type { UIMatch } from 'react-router';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';
import type { APHandle } from '~/modules/root';
import PageLayout from '@modules/professional/component/page-layout/PageLayout';
import { DetailPageButtons } from '@modules/business-manager/component/detail-page-buttons/DetailPageButtons';
import { ProfessionalRevampShell } from '~/ui-rocco/pages/professional-detail/ProfessionalRevampShell';
import { useProfessionalRevampLayoutActive } from '@modules/professional/hook/use-professional-layout-data';
import {
    PermissionEnum,
    type ProfessionalDetailsInitialFragment,
} from 'generated/graphql';
import type { Branch } from '~/modules/enquiry/util/extract-branch-from-network.server';
import type { Breadcrumb } from '@modules/professional/type/details-root.types';
import type { DropdownItem } from '@modules/enquiry/types';
import { useInView } from 'react-intersection-observer';
import { useSetEditMode } from '~/modules/root/hook/use-set-edit-mode';
import { useEditPermissions, usePermissions } from '~/modules/user';
import type { PrimaryCTAResult } from '@modules/professional/util/professional-cta-helper';
import { getBMPagePath } from '@modules/root/util/get-bm-page-path';
import type { ProfessionalBannersData } from '@modules/professional/page/OverviewPage';
import type { ProfessionalHeaderSignals } from '@modules/professional/hook/use-professional-initial-route-data';
import type { LoadedPageCtas } from '~/modules/enquiry/domain/page-ctas';
import type { loader } from '@modules/professional/page/loaders/professional-page-loader.server';
import type { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { setCloneCountryStateAtom } from '~/state/ui-state-clone-country-link';
import { useGateSubject } from '~/modules-rocco/user/auth-gate/gate-subject';
import { getProfessionalHeroImage } from '~/modules/professional/util/get-professional-hero-image';

export interface ProfessionalDetailRootData {
    initial: ProfessionalDetailsInitialFragment;
    professionalBreadcrumb: Breadcrumb[];
    banners: ProfessionalBannersData;
    branchOptions: DropdownItem<Branch>[];
    localisation: Awaited<ReturnType<typeof getPageLocalisation>>;
    primaryCTA: PrimaryCTAResult;
    headerSignals: ProfessionalHeaderSignals;
    shouldIncludeLocalBusinessSchema: boolean;
    /** Where the HDE exhibitor badge links; null hides the badge. */
    hdeExhibitorBadgeHref: string | null;
    /** The page's Enquire and Book a meeting buttons; null keeps the flat fields. */
    pageCtas?: LoadedPageCtas | null;
}

export type ProfessionalDetailRootLoaderData = typeof loader;

const ProfessionalDetailRootPage = () => {
    const { initial, primaryCTA, awards } =
        useLoaderData<ProfessionalDetailRootLoaderData>();
    const { canEditProfile } = useEditPermissions(initial.ID);
    const canCopyLink = usePermissions(
        PermissionEnum.BmAnalyticsView,
        initial.ID,
    );
    const editLink = getBMPagePath(initial.URLSegment);
    useSetEditMode(editLink, canEditProfile);
    const [footerSectionRef, footerSectionInView] = useInView({
        threshold: 0.5,
    });

    const setCountryData = useSetAtom(setCloneCountryStateAtom);
    const { setCategory } = useCurrentCategory();

    // Published once for the whole professional route — every tab, legacy and
    // revamp — so the enquiry, download and save gates show this professional
    // without each of the dozen CTAs below carrying imagery down by prop.
    useGateSubject({
        kind: 'professional',
        id: initial.ID,
        title: initial.Title,
        hero: getProfessionalHeroImage(initial.BannerImages),
    });

    // Set the atom value when the data loads
    useEffect(() => {
        setCountryData({
            urlSegment: initial?.URLSegment,
            currentCountry: initial?.Country,
            pageType: initial?.PageType,
            associatedPages: initial?.AssociatedPages,
        });
    }, [initial, setCountryData]);

    useEffect(() => {
        setCategory('Professionals');
        return () => setCategory(null);
    }, [setCategory]);

    const matches = useMatches();
    const shouldSkipLayout = matches.some(
        (match) =>
            (match as UIMatch<unknown, APHandle>)?.handle
                ?.skipProfessionalLayout === true,
    );

    // The BC-4505/BC-4572 editorial revamp owns the whole page, so skip the
    // legacy banner + tab chrome when its flag is on for any route that
    // renders the revamp layout — the overview and the revamped tabs.
    const showRevamp = useProfessionalRevampLayoutActive();

    // If a child route requests to skip the professional layout (or the revamp
    // is active), render just the Outlet — but the revamp still needs the BM
    // admin buttons (edit-mode toggle + copy link) that PageLayout would render.
    if (shouldSkipLayout || showRevamp) {
        // The revamp's persistent chrome (header + Magic Island) is shared
        // with the Business Manager preview — see ProfessionalRevampShell.
        return showRevamp ? (
            <ProfessionalRevampShell
                adminControls={
                    <DetailPageButtons
                        id={initial.ID}
                        editLink={editLink}
                        shareableLink={`/professional/${initial.URLSegment}`}
                        canEdit={canEditProfile}
                        canCopyLink={canCopyLink}
                    />
                }
            >
                <Outlet />
            </ProfessionalRevampShell>
        ) : (
            <Outlet />
        );
    }

    return (
        <PageLayout
            primaryCTA={primaryCTA}
            awards={awards}
            canCarouselControl={canEditProfile}
        >
            <DetailPageButtons
                id={initial.ID}
                editLink={editLink}
                shareableLink={`/professional/${initial.URLSegment}`}
                canEdit={canEditProfile}
                canCopyLink={canCopyLink}
                showButton={!footerSectionInView}
            />
            <Outlet />
            <div ref={footerSectionRef} />
        </PageLayout>
    );
};

export default ProfessionalDetailRootPage;
